import { skipToken, useSuspenseQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import { green, grey, purple } from "@mui/material/colors";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { BarChart } from "components/Dashboard/BarChart";
import ChartLegend from "components/Dashboard/ChartLegend";
import DonutChart from "components/Dashboard/DonutChart";
import { formatPercent, NO_MATCHED_VENDOR_LABEL } from "encamp-shared/src/waste/vendorStatsFormat";
import {
  DiscrepancyFlagSource,
  ManifestDiscrepancyFlaggedSourceStatsByMonthQuery,
  ManifestDiscrepancyFlaggedSourceStatsByVendorQuery,
  ManifestDiscrepancyFlaggedSourceStatsQuery,
} from "generated-graphql/graphql";
import { useQueryParams } from "hooks/useQueryParams";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

import {
  MANIFEST_DISCREPANCY_FLAGGED_SOURCE_STATS_BY_MONTH_QUERY,
  MANIFEST_DISCREPANCY_FLAGGED_SOURCE_STATS_BY_VENDOR_QUERY,
  MANIFEST_DISCREPANCY_FLAGGED_SOURCE_STATS_QUERY,
} from "./api";
import {
  closureDataEmptyMessage,
  CORRECTION_TRACKING_START_YEAR,
  formatDays,
  lastVisibleMonth,
  MONTH_LABELS,
  monthDateRange,
} from "./correctionTracking";
import { removeOmnisearchTerm, upsertOmnisearchTerm } from "./omnisearch";

// Source identity colors, used consistently across the monthly mix chart and
// table accents: the regulator wears the app's primary blue, Encamp a purple
// that can't be confused with the good/bad greens and oranges used elsewhere
// on the dashboard.
export const FLAG_SOURCE_COLORS = {
  regulator: "primary" as const, // resolved from theme at render time
  encamp: purple[300],
} as const;

const SOURCE_LABELS: Record<DiscrepancyFlagSource, string> = {
  [DiscrepancyFlagSource.Regulator]: "EPA / Regulator",
  [DiscrepancyFlagSource.Encamp]: "Encamp",
};

interface SummaryProps {
  shippedYear: number;
  skip?: boolean;
  selectedVendor?: string | null;
}

type SourceStat =
  ManifestDiscrepancyFlaggedSourceStatsQuery["manifestDiscrepancyFlaggedSourceStats"][number];

// Overall comparison: who flags discrepancies (regulator vs Encamp) and how
// many get corrected — grouped total/corrected bars per source, with the
// correction-rate detail captioned beneath. Mirrors the compliance team's
// spreadsheet chart. Suspense-based like its sibling summaries.
export function FlaggedSourceSummary({
  shippedYear,
  skip = false,
  selectedVendor = null,
}: SummaryProps) {
  const { tenantId } = useTenant();

  const { data, error } = useSuspenseQuery(
    MANIFEST_DISCREPANCY_FLAGGED_SOURCE_STATS_QUERY,
    skip || !tenantId
      ? skipToken
      : {
          variables: { tenantId, shippedYear, vendorSource: selectedVendor },
          errorPolicy: "all",
        }
  );

  if (error) {
    return <Typography color="error">Couldn't load flagged-source stats</Typography>;
  }

  const stats = data?.manifestDiscrepancyFlaggedSourceStats;
  if (!stats) {
    return null;
  }

  const hasData = stats.some((s) => s.total > 0);
  if (!hasData) {
    const emptyMessage =
      selectedVendor && shippedYear >= CORRECTION_TRACKING_START_YEAR
        ? `No flagged discrepancies for ${selectedVendor} in ${shippedYear}.`
        : closureDataEmptyMessage(shippedYear);
    return (
      <Stack spacing={0.5}>
        <Typography variant="h3">—</Typography>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Stack>
    );
  }

  return <SourceComparisonChart stats={stats} />;
}

// Option A donut composition for one flag source. The ring is the *engaged*
// flagged population (UNRESOLVED backlog is already excluded upstream): green
// fills the corrected share, and the grey remainder is the not-yet-corrected
// (PENDING) tail — shown neutrally, not as failure. Exported for unit testing.
export function buildSourceDonutSegments(stat: SourceStat) {
  const corrected = stat.corrected;
  const remainder = Math.max(stat.total - corrected, 0);
  return [
    {
      key: "corrected",
      value: corrected,
      color: green[400],
      label: `Corrected in e-Manifest (${corrected})`,
    },
    {
      key: "remainder",
      value: remainder,
      color: grey[300],
      label: `Not yet corrected (${remainder})`,
    },
  ];
}

// Two donuts side by side — one per flag source — so the corrected count reads
// as a proportion of what that source flagged. Shared legend above; each
// donut's center is the source's correction rate, with mean-time-to-close and
// ≤30d detail beneath.
function SourceComparisonChart({ stats }: { stats: readonly SourceStat[] }) {
  const ordered = orderEncampFirst(stats);
  return (
    // flexGrow + justifyContent vertically center against the taller monthly
    // chart beside it, mirroring the other summaries.
    <Stack spacing={1.5} sx={{ flexGrow: 1, justifyContent: "center" }}>
      <ChartLegend
        config={{ direction: "row", position: "above" }}
        items={[
          { color: green[400], value: "Corrected in e-Manifest" },
          { color: grey[300], value: "Not yet corrected" },
        ]}
      />
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "space-around", alignItems: "flex-start" }}
      >
        {ordered.map((s) => (
          <SourceDonut key={s.source} stat={s} />
        ))}
      </Stack>
    </Stack>
  );
}

// Encamp first (left) across the flagged-source visuals — tells the story that
// Encamp surfaces discrepancies before the regulator flags them.
function orderEncampFirst<T extends { source: DiscrepancyFlagSource }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.source === b.source) return 0;
    return a.source === DiscrepancyFlagSource.Encamp ? -1 : 1;
  });
}

function SourceDonut({ stat }: { stat: SourceStat }) {
  const segments = buildSourceDonutSegments(stat);

  return (
    <Stack sx={{ alignItems: "center", flex: 1, minWidth: 0 }} spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {SOURCE_LABELS[stat.source]}
      </Typography>
      {stat.total > 0 ? (
        <DonutChart
          containerHeight="11rem"
          showLegend={false}
          // DonutChart's default 120px outer radius (240px diameter) doesn't
          // fit the 11rem (176px) container — the arcs clip into a square.
          // Shrink to a 160px-diameter ring with the default's proportions.
          seriesOverrides={{ innerRadius: 45, outerRadius: 80 }}
          seriesData={segments.map((seg) => ({
            id: seg.key,
            value: seg.value,
            label: seg.label,
            color: seg.color,
          }))}
          colors={segments.map((seg) => seg.color)}
          label={formatPercent(stat.corrected, stat.total)}
          subLabel="corrected"
        />
      ) : (
        <Typography variant="h3" color="text.secondary" sx={{ py: 4 }}>
          —
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" textAlign="center">
        {stat.total} flagged
        {stat.avgDaysToClose != null && ` · avg ${formatDays(stat.avgDaysToClose)}d`}
        {` · ${formatPercent(stat.corrected30, stat.total)} ≤30d`}
      </Typography>
    </Stack>
  );
}

interface BreakdownProps {
  shippedYear: number;
  view: "byVendor" | "byMonth";
  skip?: boolean;
  selectedVendor?: string | null;
  onSelectVendor?: (vendor: string | null) => void;
}

export function FlaggedSourceBreakdown({
  shippedYear,
  view,
  skip = false,
  selectedVendor = null,
  onSelectVendor,
}: BreakdownProps) {
  const { tenantId } = useTenant();
  const { setFilters } = useQueryParams<{ omnisearch?: string }>();

  const vendorQuery = useSuspenseQuery(
    MANIFEST_DISCREPANCY_FLAGGED_SOURCE_STATS_BY_VENDOR_QUERY,
    skip || !tenantId || view !== "byVendor"
      ? skipToken
      : { variables: { tenantId, shippedYear }, errorPolicy: "all" }
  );
  // The monthly mix narrows to the selected vendor directly (no overlay —
  // with two source series an overlay would double the bar count).
  const monthQuery = useSuspenseQuery(
    MANIFEST_DISCREPANCY_FLAGGED_SOURCE_STATS_BY_MONTH_QUERY,
    skip || !tenantId || view !== "byMonth"
      ? skipToken
      : {
          variables: { tenantId, shippedYear, vendorSource: selectedVendor },
          errorPolicy: "all",
        }
  );

  const handleVendorClick = (vendor: string) => {
    const togglingOff = vendor === selectedVendor;
    setFilters((prev) => ({
      ...prev,
      // Toggling narrows the grid to this vendor (or removes the term on
      // toggle-off); the year scope stays the year picker's hidden default.
      omnisearch: togglingOff
        ? removeOmnisearchTerm(prev.omnisearch ?? "", "vendor")
        : upsertOmnisearchTerm(prev.omnisearch ?? "", "vendor", vendor),
    }));
    onSelectVendor?.(togglingOff ? null : vendor);
  };

  const filterByMonth = (month: number) => {
    setFilters((prev) => ({
      ...prev,
      // Bars bucket by the discrepancy raised date, so drill in by that axis.
      // The raised-date term supersedes the grid's hidden shipped-year default
      // (see Manifests.tsx), keeping cross-year rows without a visible
      // shippedDate override.
      omnisearch: upsertOmnisearchTerm(
        prev.omnisearch ?? "",
        "flaggedRaisedAt",
        monthDateRange(shippedYear, month)
      ),
    }));
  };

  return (
    <>
      {view === "byVendor" && (
        <SourceVendorTable
          shippedYear={shippedYear}
          error={vendorQuery.error}
          data={vendorQuery.data}
          selectedVendor={selectedVendor}
          onVendorClick={handleVendorClick}
        />
      )}
      {view === "byMonth" && (
        <MonthlySourceMixBars
          shippedYear={shippedYear}
          error={monthQuery.error}
          data={monthQuery.data}
          onMonthClick={filterByMonth}
        />
      )}
    </>
  );
}

type VendorRow =
  ManifestDiscrepancyFlaggedSourceStatsByVendorQuery["manifestDiscrepancyFlaggedSourceStatsByVendor"][number];

function SourceVendorTable({
  shippedYear,
  error,
  data,
  selectedVendor,
  onVendorClick,
}: {
  shippedYear: number;
  error: unknown;
  data: ManifestDiscrepancyFlaggedSourceStatsByVendorQuery | undefined;
  selectedVendor: string | null;
  onVendorClick: (vendor: string) => void;
}) {
  if (error) {
    return <Typography color="error">Couldn't load vendor flagged-source stats</Typography>;
  }

  const rows = data?.manifestDiscrepancyFlaggedSourceStatsByVendor ?? [];
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {closureDataEmptyMessage(shippedYear)}
      </Typography>
    );
  }

  return (
    <TableContainer
      sx={{
        maxHeight: "16rem",
        "& .MuiTableCell-stickyHeader": { backgroundColor: "background.paper" },
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Vendor</TableCell>
            <TableCell align="right">Encamp</TableCell>
            <TableCell align="right">Encamp % corrected</TableCell>
            <TableCell align="right">EPA / Regulator</TableCell>
            <TableCell align="right">Regulator % corrected</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <SourceVendorRow
              key={row.vendor ?? `__unmatched-${index}`}
              row={row}
              selected={row.vendor !== null && row.vendor === selectedVendor}
              onVendorClick={onVendorClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SourceVendorRow({
  row,
  selected,
  onVendorClick,
}: {
  row: VendorRow;
  selected: boolean;
  onVendorClick: (vendor: string) => void;
}) {
  const theme = useTheme();
  const vendor = row.vendor;
  const clickable = vendor !== null && vendor !== undefined;

  const selectedSx = selected
    ? {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        "& > *:first-of-type": { fontWeight: 600 },
      }
    : {
        borderLeft: "4px solid transparent",
      };

  return (
    <TableRow
      hover={clickable}
      selected={selected}
      onClick={clickable ? () => onVendorClick(vendor) : undefined}
      sx={{
        ...selectedSx,
        ...(clickable ? { cursor: "pointer" } : {}),
      }}
    >
      <TableCell>
        {clickable ? (
          vendor
        ) : (
          <Typography variant="body2" color="text.secondary" component="span">
            {NO_MATCHED_VENDOR_LABEL}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{row.encampTotal}</TableCell>
      <TableCell align="right">{formatPercent(row.encampCorrected, row.encampTotal)}</TableCell>
      <TableCell align="right">{row.regulatorTotal}</TableCell>
      <TableCell align="right">
        {formatPercent(row.regulatorCorrected, row.regulatorTotal)}
      </TableCell>
    </TableRow>
  );
}

type MonthRow =
  ManifestDiscrepancyFlaggedSourceStatsByMonthQuery["manifestDiscrepancyFlaggedSourceStatsByMonth"][number];

function MonthlySourceMixBars({
  shippedYear,
  error,
  data,
  onMonthClick,
}: {
  shippedYear: number;
  error: unknown;
  data: ManifestDiscrepancyFlaggedSourceStatsByMonthQuery | undefined;
  onMonthClick: (month: number) => void;
}) {
  const theme = useTheme();
  // Fill every visible month so the axis has no gaps; empty months render
  // zero-height stacks.
  const dataset = useMemo(() => {
    const byMonth = new Map<number, MonthRow>();
    (data?.manifestDiscrepancyFlaggedSourceStatsByMonth ?? []).forEach((row) => {
      byMonth.set(row.month, row);
    });
    const visibleMonths = lastVisibleMonth(shippedYear, [...byMonth.keys()]);
    return MONTH_LABELS.slice(0, visibleMonths).map((label, i) => {
      const month = i + 1;
      const row = byMonth.get(month);
      return {
        month,
        monthLabel: label,
        regulator: row?.regulatorTotal ?? 0,
        regulatorCorrected: row?.regulatorCorrected ?? 0,
        encamp: row?.encampTotal ?? 0,
        encampCorrected: row?.encampCorrected ?? 0,
        total: (row?.regulatorTotal ?? 0) + (row?.encampTotal ?? 0),
      };
    });
  }, [data, shippedYear]);

  if (error) {
    return <Typography color="error">Couldn't load monthly flagged-source mix</Typography>;
  }

  const hasData = dataset.some((d) => d.total > 0);
  const regulatorColor = theme.palette.primary.main;
  const encampColor = FLAG_SOURCE_COLORS.encamp;

  // One stacked bar per month showing the mix of who flagged what; the
  // tooltip carries each source's corrected count alongside its total.
  // Encamp first (bottom of the stack, first in the legend) — matches the
  // Encamp-first ordering across the flagged-source visuals.
  const series = [
    {
      type: "bar" as const,
      id: "encamp",
      dataKey: "encamp",
      stack: "mix",
      color: encampColor,
      valueFormatter: (val: number | null, context: { dataIndex: number }) => {
        if (!val) return "";
        const corrected = dataset[context.dataIndex]?.encampCorrected ?? 0;
        return `Encamp: ${val} flagged (${corrected} corrected)`;
      },
    },
    {
      type: "bar" as const,
      id: "regulator",
      dataKey: "regulator",
      stack: "mix",
      color: regulatorColor,
      valueFormatter: (val: number | null, context: { dataIndex: number }) => {
        if (!val) return "";
        const corrected = dataset[context.dataIndex]?.regulatorCorrected ?? 0;
        return `EPA / Regulator: ${val} flagged (${corrected} corrected)`;
      },
    },
  ];

  return (
    <Stack spacing={1.5} sx={{ position: "relative" }}>
      <ChartLegend
        config={{ direction: "row", position: "above" }}
        items={[
          { color: encampColor, value: "Encamp" },
          { color: regulatorColor, value: "EPA / Regulator" },
        ]}
        sx={{ paddingLeft: 6 }}
      />
      <Box height={theme.spacing(40)}>
        <BarChart
          dataset={dataset}
          noData={!hasData}
          noDataMessage={closureDataEmptyMessage(shippedYear)}
          series={series}
          xAxis={[{ scaleType: "band", dataKey: "monthLabel", categoryGapRatio: 0.4 }]}
          yAxis={[
            {
              scaleType: "linear",
              valueFormatter: (val) => (val ? val.toString() : "0"),
            },
          ]}
          onItemClick={(_event, item) => {
            const row = dataset[item.dataIndex];
            if (row && row.total > 0) {
              onMonthClick(row.month);
            }
          }}
          stackedTooltipTotalFormatter={(_total, dataIndex) =>
            `Total flagged: ${dataset[dataIndex]?.total ?? 0}`
          }
        />
      </Box>
    </Stack>
  );
}
